import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, categories, siteConfig } from '@/mocks/welfare';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <SEOHead
          title="文章未找到 - 福利导航网"
          description="抱歉，您访问的文章不存在。"
          keywords={siteConfig.keywords}
          canonicalUrl={`${siteConfig.url}/article/${id}`}
        />
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">您访问的文章不存在或已被删除</p>
            <Link to="/" className="px-6 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.id === article.category);
  const relatedArticles = articles
    .filter((a) => a.id !== article.id && (a.category === article.category || a.tags.some((t) => article.tags.includes(t))))
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.summary,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.publishDate,
        image: article.imageUrl,
        publisher: {
          '@type': 'Organization',
          name: siteConfig.name,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${siteConfig.url}/article/${article.id}`,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 福利导航网`}
        description={article.summary}
        keywords={`${siteConfig.keywords},${article.tags.join(',')}`}
        canonicalUrl={`${siteConfig.url}/article/${article.id}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: cat?.name || '文章', href: cat ? `/category/${cat.id}` : '/category/all' },
              { label: article.title },
            ]}
          />

          <div className="mt-6">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {cat && (
                <span className="text-xs bg-accent-100 text-accent-700 px-3 py-1 rounded-full font-medium">{cat.name}</span>
              )}
              <span className="text-xs text-foreground-400">{article.publishDate}</span>
              <span className="text-xs text-foreground-400">作者：{article.author}</span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs bg-background-100 text-foreground-500 px-2.5 py-1 rounded-md">{tag}</span>
              ))}
            </div>

            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                title={`${article.title} - 福利导航网`}
                className="w-full h-64 md:h-80 object-cover object-top"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-4
                [&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-foreground-800 [&_h4]:mt-6 [&_h4]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:mb-4 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:space-y-1
                [&_li]:text-foreground-600 [&_li]:leading-relaxed
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.contentHtml }}
            />
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">相关文章推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="bg-background-50 rounded-xl border border-background-200 overflow-hidden hover:border-primary-300 transition-all duration-300 group"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        title={`${ra.title} - 福利导航网`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-foreground-900 text-sm leading-snug mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">{ra.title}</h3>
                      <p className="text-xs text-foreground-400">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}